// Video clip player over the notes (BRIEFS C6.1).
//
// Plays one part (start..end) of a YouTube video with the YouTube IFrame API (react-youtube):
// playerVars {start, end, rel: 0, modestbranding: 1}. Close with X or Esc; speed menu
// 0.75–2×; full-screen button (the whole player box goes full screen, so "Finished" still
// shows there). When the time passes `end` the video is paused and "Finished" + Close shows.
//
// Activity: video_open when it opens; video_watch with the seconds ACTUALLY played (the
// player clock moving forward while playing — a jump/seek is not counted) every 15 s and on
// close; video_close on close. While it plays the area is "video" and markActive() keeps
// the active-time clock running without mouse/keyboard input.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import YouTube from 'react-youtube';
import { markActive, track, useActivityArea } from './activity';
import { C, FONT } from './theme';

const SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2];
const WATCH_EVERY_MS = 15000;
const TICK_MS = 500;

export function clipMinutes(clip) {
  if (!clip || clip.end == null) return null;
  return Math.max(1, Math.round((clip.end - (clip.start || 0)) / 60));
}

// Rendered only while the video plays: its time goes to the "video" area.
function VideoArea() {
  useActivityArea('video');
  return null;
}

const IconClose = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
);
const IconFull = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></svg>
);

const barBtn = {
  display: 'flex', alignItems: 'center', gap: 8, height: 44, minWidth: 44, padding: '0 14px',
  border: '1px solid rgba(255,255,255,0.35)', borderRadius: 8, background: 'transparent',
  color: '#FFFFFF', fontFamily: FONT.ui, fontSize: 14, cursor: 'pointer', justifyContent: 'center',
};

export default function VideoModal({ clip, chapterId, unitCode, unitLabel, onClose }) {
  const playerRef = useRef(null);
  const boxRef = useRef(null);
  const closeRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [finished, setFinished] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [failed, setFailed] = useState(false);
  const w = useRef({ last: null, pending: 0, total: 0, playing: false, finished: false });
  const mins = clipMinutes(clip);
  const start = clip.start || 0;
  const end = clip.end;

  const base = useCallback(() => ({
    video_clip_id: clip.id,
    chapter_id: chapterId != null ? Number(chapterId) : undefined,
    section_code: unitCode || undefined,
  }), [clip.id, chapterId, unitCode]);

  const sendWatch = useCallback(() => {
    const s = w.current;
    const secs = Math.round(s.pending);
    if (secs < 1) return;
    s.pending -= secs;
    let position = null;
    try { position = Math.round(playerRef.current.getCurrentTime()); } catch { /* not ready */ }
    track('video_watch', { ...base(), duration_ms: secs * 1000, details: { seconds: secs, position } });
  }, [base]);

  // open / close events; 15-second watch reports
  useEffect(() => {
    track('video_open', { ...base(), details: { start, end, youtube_id: clip.youtube_id, kind: clip.kind === 'question' ? 'question' : 'concept' } });
    const s = w.current;
    const every = setInterval(sendWatch, WATCH_EVERY_MS);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      clearInterval(every);
      sendWatch();
      track('video_close', { ...base(), details: { watched_seconds: Math.round(s.total), finished: s.finished } });
      document.body.style.overflow = prevOverflow;
      try { if (document.fullscreenElement) document.exitFullscreen(); } catch { /* ignore */ }
    };
  }, []);

  // count the seconds actually played; stop at `end`
  useEffect(() => {
    const t = setInterval(() => {
      const s = w.current;
      const p = playerRef.current;
      if (!p || !s.playing) { s.last = null; return; }
      let now;
      try { now = p.getCurrentTime(); } catch { return; }
      if (typeof now !== 'number') return;
      const capped = end != null ? Math.min(now, end) : now;    // never count past the end
      if (s.last != null) {
        const d = capped - s.last;
        if (d > 0 && d <= 3 * speed) { s.pending += d; s.total += d; }   // a seek is not counted
      }
      s.last = capped;
      markActive();
      if (end != null && now >= end - 0.25) {
        try { p.pauseVideo(); } catch { /* ignore */ }
        s.playing = false;
        s.finished = true;
        setPlaying(false);
        setFinished(true);
      }
    }, TICK_MS);
    return () => clearInterval(t);
  }, [end, speed]);

  // Esc closes (when not full screen: there Esc first leaves full screen)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !document.fullscreenElement) { e.preventDefault(); onClose(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => { if (closeRef.current) closeRef.current.focus(); }, []);

  const onReady = (e) => {
    playerRef.current = e.target;
    try { e.target.setPlaybackRate(speed); } catch { /* ignore */ }
  };
  const onStateChange = (e) => {
    const st = e.data;      // 1 playing, 2 paused, 0 ended, 3 buffering
    const s = w.current;
    if (st === 1) {
      if (s.finished) { s.finished = false; setFinished(false); }
      s.playing = true; setPlaying(true);
    } else if (st === 0) {
      s.playing = false; s.finished = true; setPlaying(false); setFinished(true);
    } else if (st === 2) {
      s.playing = false; setPlaying(false);
    }
  };
  const changeSpeed = (v) => {
    setSpeed(v);
    try { playerRef.current.setPlaybackRate(v); } catch { /* ignore */ }
  };
  const fullScreen = () => {
    const el = boxRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
  };

  const opts = {
    width: '100%', height: '100%',
    playerVars: {
      start, ...(end != null ? { end } : {}), rel: 0, modestbranding: 1, autoplay: 1,
      playsinline: 1,
    },
  };

  return (
    <div data-testid="v2-video-modal" role="dialog" aria-modal="true" aria-label={`Video: ${clip.title}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(15,23,42,0.82)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, boxSizing: 'border-box',
      }}>
      {playing && <VideoArea />}
      <div style={{
        width: 'min(1000px, 100%)', display: 'flex', flexDirection: 'column', gap: 12,
        background: C.bar, borderRadius: 14, padding: 16, boxSizing: 'border-box', color: '#FFFFFF',
        boxShadow: '0 20px 50px rgba(0,0,0,0.35)', fontFamily: FONT.ui,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flexGrow: 1, minWidth: 0 }}>
            <div data-testid="v2-video-title" style={{ fontFamily: FONT.heading, fontSize: 18, fontWeight: 600, lineHeight: 1.3 }}>
              {clip.title || 'Video'}
            </div>
            <div style={{ fontSize: 13, color: '#C7D2FE', marginTop: 2 }}>
              {unitLabel ? `${unitLabel} · ` : ''}{mins ? `${mins} min` : ''}
            </div>
          </div>
          <label style={{ ...barBtn, cursor: 'default', gap: 6 }}>
            <span>Speed</span>
            <select data-testid="v2-video-speed" value={speed} aria-label="Speed"
              onChange={(e) => changeSpeed(Number(e.target.value))} style={{
                height: 30, border: 0, borderRadius: 6, fontFamily: 'inherit', fontSize: 14,
                background: '#FFFFFF', color: C.text, padding: '0 4px',
              }}>
              {SPEEDS.map((v) => <option key={v} value={v}>{v}×</option>)}
            </select>
          </label>
          <button type="button" data-testid="v2-video-full" onClick={fullScreen} style={barBtn}
            title="Full screen" aria-label="Full screen">
            <IconFull /><span>Full screen</span>
          </button>
          <button type="button" ref={closeRef} data-testid="v2-video-close" onClick={onClose}
            style={barBtn} title="Close (Esc)" aria-label="Close video">
            <IconClose />
          </button>
        </div>
        <div ref={boxRef} style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', background: '#000', borderRadius: 8, overflow: 'hidden' }}>
          <YouTube videoId={clip.youtube_id} opts={opts} onReady={onReady} onStateChange={onStateChange}
            onError={() => setFailed(true)} style={{ position: 'absolute', inset: 0 }}
            iframeClassName="v2-video-frame" />
          {(finished || failed) && (
            <div data-testid="v2-video-finished" style={{
              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 16, background: 'rgba(15,23,42,0.88)', color: '#FFFFFF',
            }}>
              <div style={{ fontFamily: FONT.heading, fontSize: 24, fontWeight: 600 }}>
                {failed && !finished ? 'This video could not play.' : 'Finished'}
              </div>
              <button type="button" data-testid="v2-video-finished-close" onClick={onClose} style={{
                height: 44, padding: '0 22px', border: 0, borderRadius: 22, background: C.primary,
                color: '#FFFFFF', fontFamily: FONT.ui, fontSize: 15, fontWeight: 600, cursor: 'pointer',
              }}>Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
